/**
 * The API client.
 *
 * Types come from `schema.d.ts`, generated from the backend's OpenAPI document
 * (requirement 13.4) — never hand-maintained, so a backend change that breaks
 * the frontend fails typechecking rather than at runtime.
 *
 * Two behaviours worth knowing:
 *
 * - The session lives in an HttpOnly cookie the browser attaches automatically.
 *   The CSRF token cannot live there (the client has to read it), so it is held
 *   in memory and echoed in a header on every unsafe request.
 * - Errors arrive in one envelope, so `ApiError` always carries a machine
 *   readable code and any findings rather than a bare status.
 */

import type { components, paths } from "./schema";

export const CSRF_HEADER = "X-Fluidea-CSRF";

export interface Finding {
  code: string;
  message: string;
  severity: string;
  path: string | null;
  detail: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly findings: Finding[];
  readonly detail: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    findings: Finding[] = [],
    detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.findings = findings;
    this.detail = detail;
  }

  /** True when the draft moved under us — show what changed, do not merge. */
  get isConflict(): boolean {
    return this.code === "revision_conflict";
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

// Held in memory rather than storage: it should not outlive the tab, and a
// session cookie without it cannot be used cross-origin.
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  formData?: FormData;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const url = new URL(path, window.location.origin);

  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const headers: Record<string, string> = {};
  if (UNSAFE_METHODS.has(method) && csrfToken) {
    headers[CSRF_HEADER] = csrfToken;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const response = await fetch(url.toString(), {
    method,
    headers,
    body,
    // Same-origin only: there is no cross-origin case to support.
    credentials: "same-origin",
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return (await response.blob()) as T;
  }
  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as {
      error?: {
        code?: string;
        message?: string;
        findings?: Finding[];
        detail?: Record<string, unknown>;
      };
    };
    const error = body.error ?? {};
    return new ApiError(
      response.status,
      error.code ?? "unknown",
      error.message ?? response.statusText,
      error.findings ?? [],
      error.detail ?? {},
    );
  } catch {
    return new ApiError(response.status, "unknown", response.statusText);
  }
}

// --- Typed shorthands ------------------------------------------------------
//
// Response types are pulled from the generated schema so a backend change shows
// up here as a type error.

type Json<T> = T extends { content: { "application/json": infer B } } ? B : never;
type Ok<T> = T extends { responses: infer R }
  ? R extends { 200: infer S }
    ? Json<S>
    : R extends { 201: infer S }
      ? Json<S>
      : R extends { 202: infer S }
        ? Json<S>
        : never
  : never;

export type SessionResponse = Ok<paths["/api/auth/login"]["post"]>;
export type CurrentUser = Ok<paths["/api/auth/me"]["get"]>;
export type Program = Ok<paths["/api/programs/{program_id}"]["get"]>;
export type Project = Ok<paths["/api/projects/{project_id}"]["get"]>;
export type Design = Ok<paths["/api/designs/{design_id}"]["get"]>;
/** A display units setting as the server takes it (D49): null inherits every quantity. */
export type DisplayUnitsBody = components["schemas"]["DisplayUnits"] | null;
export type Draft = Ok<paths["/api/designs/{design_id}/draft"]["get"]>;
export type Version = Ok<paths["/api/designs/{design_id}/versions/{version_id}"]["get"]>;
export type VersionSummary = Ok<paths["/api/designs/{design_id}/versions"]["get"]>[number];
export type Diff = Ok<paths["/api/designs/{design_id}/diff"]["get"]>;
export type ActivityEntry = Ok<paths["/api/activity"]["get"]>[number];
export type Job = Ok<paths["/api/jobs/{job_id}"]["get"]>;
export type LibraryEntity = Ok<paths["/api/library/{kind}"]["get"]>[number];
export type LibraryKind = LibraryEntity["kind"];
export type LibraryVersion = Ok<paths["/api/library/versions/{version_id}"]["get"]>;
export type CheckReport = Ok<paths["/api/designs/{design_id}/check"]["get"]>;
export type LibraryEntityDetail = Ok<paths["/api/library/entities/{entity_id}"]["get"]>;
export type LibraryDraft = Ok<paths["/api/library/entities/{entity_id}/draft"]["get"]>;
export type LibraryVersionSummary = Ok<paths["/api/library/entities/{entity_id}/versions"]["get"]>[number];
export type SavedBlock = Ok<paths["/api/designs/{design_id}/blocks"]["post"]>;
export type PlacedBlock = Ok<paths["/api/designs/{design_id}/blocks/place"]["post"]>;
export type DetachedBlock = Ok<paths["/api/designs/{design_id}/blocks/{instance_local_id}/detach"]["post"]>;
export type UpdatedBlock = Ok<paths["/api/designs/{design_id}/blocks/{instance_local_id}/update"]["post"]>;
export type BlockUpdate = Ok<paths["/api/designs/{design_id}/blocks/updates"]["get"]>[number];
export type Account = Ok<paths["/api/users"]["get"]>[number];
export type Protocol = Ok<paths["/api/protocols/{entity_id}"]["get"]>;
export type ProtocolKind = Protocol["kind"];
export type ProtocolDraft = Ok<paths["/api/protocols/{entity_id}/draft"]["get"]>;
export type ProtocolCheck = Ok<paths["/api/protocols/{entity_id}/check"]["get"]>;

export const api = {
  async logIn(email: string, password: string): Promise<SessionResponse> {
    const session = await request<SessionResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });
    setCsrfToken(session.csrf_token);
    return session;
  },

  async logOut(): Promise<void> {
    await request<void>("/api/auth/logout", { method: "POST" });
    setCsrfToken(null);
  },

  async currentUser(): Promise<CurrentUser> {
    const user = await request<CurrentUser>("/api/auth/me");
    // Recovers the CSRF token after a reload, when the cookie survives but
    // in-memory state does not.
    setCsrfToken(user.csrf_token);
    return user;
  },

  // --- One's own account (D37) ---------------------------------------------

  /** Change one's display name. The email is the sign-in and stays put. */
  updateProfile(displayName: string): Promise<CurrentUser> {
    return request<CurrentUser>("/api/auth/me", { method: "PATCH", body: { display_name: displayName } });
  },

  /** Needs the current password. Ends every session, this one included. */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request<void>("/api/auth/password", {
      method: "POST",
      body: { current_password: currentPassword, new_password: newPassword },
    });
    setCsrfToken(null);
  },

  signOutElsewhere(): Promise<{ sessions_revoked: number }> {
    return request<{ sessions_revoked: number }>("/api/auth/sessions/revoke-others", { method: "POST" });
  },

  // --- Accounts, for administrators (D37) --------------------------------------

  listAccounts(): Promise<Account[]> {
    return request<Account[]>("/api/users");
  },

  createAccount(account: { email: string; display_name: string; password: string; is_admin: boolean }): Promise<Account> {
    return request<Account>("/api/users", { method: "POST", body: account });
  },

  updateAccount(userId: string, change: { is_active?: boolean; is_admin?: boolean }): Promise<Account> {
    return request<Account>(`/api/users/${userId}`, { method: "PATCH", body: change });
  },

  resetPassword(userId: string, password: string): Promise<Account> {
    return request<Account>(`/api/users/${userId}/password`, { method: "POST", body: { password } });
  },

  revokeAccountSessions(userId: string): Promise<{ sessions_revoked: number }> {
    return request<{ sessions_revoked: number }>(`/api/users/${userId}/sessions/revoke`, { method: "POST" });
  },

  listPrograms(): Promise<Program[]> {
    return request<Program[]>("/api/programs");
  },

  createProgram(name: string, description = ""): Promise<Program> {
    return request<Program>("/api/programs", {
      method: "POST",
      body: { name, description },
    });
  },

  listProjects(): Promise<Project[]> {
    return request<Project[]>("/api/projects");
  },

  /** Archived projects only (D38): out of the list, nothing removed. */
  listArchivedProjects(): Promise<Project[]> {
    return request<Project[]>("/api/projects", { query: { archived: "true" } });
  },

  /** Take a project out of the list; reversible (D38). Owners only. */
  archiveProject(projectId: string): Promise<Project> {
    return request<Project>(`/api/projects/${projectId}/archive`, { method: "POST" });
  },

  /** Into a programme, another one, or none (null). Changes who inherits access. */
  moveProject(projectId: string, programId: string | null): Promise<Project> {
    return request<Project>(`/api/projects/${projectId}/program`, { method: "PUT", body: { program_id: programId } });
  },

  restoreProject(projectId: string): Promise<Project> {
    return request<Project>(`/api/projects/${projectId}/restore`, { method: "POST" });
  },

  getProject(projectId: string): Promise<Project> {
    return request<Project>(`/api/projects/${projectId}`);
  },

  createProject(name: string, programId?: string): Promise<Project> {
    return request<Project>("/api/projects", {
      method: "POST",
      body: { name, description: "", program_id: programId ?? null },
    });
  },

  listDesigns(projectId: string): Promise<Design[]> {
    return request<Design[]>(`/api/projects/${projectId}/designs`);
  },

  /** A new design of one kind, which it keeps for good (D34). */
  createDesign(projectId: string, name: string, kind: Design["kind"] = "microfluidic"): Promise<Design> {
    return request<Design>(`/api/projects/${projectId}/designs`, {
      method: "POST",
      body: { name, manufacturing_profile_version_id: null, kind },
    });
  },

  /** Permanently delete a design that was never published (D33). Owners only. */
  deleteDesign(designId: string): Promise<void> {
    return request<void>(`/api/designs/${designId}`, { method: "DELETE" });
  },

  /** Permanently delete a project of unpublished work (D33). Owners only. */
  deleteProject(projectId: string): Promise<void> {
    return request<void>(`/api/projects/${projectId}`, { method: "DELETE" });
  },

  getDesign(designId: string): Promise<Design> {
    return request<Design>(`/api/designs/${designId}`);
  },

  /** A competing variant from a published version, recording where it branched (D9). */
  forkDesign(designId: string, sourceVersionId: string, name: string, targetProjectId?: string): Promise<Design> {
    return request<Design>(`/api/designs/${designId}/fork`, {
      method: "POST",
      body: { source_version_id: sourceVersionId, name, target_project_id: targetProjectId ?? null },
    });
  },

  /** A new design from another's working draft as it stands. No lineage: nothing citable was copied. */
  copyDesign(designId: string, name: string, targetProjectId?: string): Promise<Design> {
    return request<Design>(`/api/designs/${designId}/copy`, {
      method: "POST",
      body: { name, target_project_id: targetProjectId ?? null },
    });
  },

  // --- Scripts and condition sets (D43) ------------------------------------------

  listProtocols(designId: string): Promise<Protocol[]> {
    return request<Protocol[]>(`/api/designs/${designId}/protocols`);
  },

  createProtocol(designId: string, kind: ProtocolKind, name: string, content?: Record<string, unknown>): Promise<Protocol> {
    return request<Protocol>(`/api/designs/${designId}/protocols`, {
      method: "POST",
      body: { kind, name, content: content ?? null },
    });
  },

  renameProtocol(id: string, name: string): Promise<Protocol> {
    return request<Protocol>(`/api/protocols/${id}`, { method: "PATCH", body: { name } });
  },

  /** Permanently, if never published (D33). */
  deleteProtocol(id: string): Promise<void> {
    return request<void>(`/api/protocols/${id}`, { method: "DELETE" });
  },

  getProtocolDraft(id: string): Promise<ProtocolDraft> {
    return request<ProtocolDraft>(`/api/protocols/${id}/draft`);
  },

  updateProtocolDraft(id: string, content: Record<string, unknown>, expectedRevision: number): Promise<ProtocolDraft> {
    return request<ProtocolDraft>(`/api/protocols/${id}/draft`, {
      method: "PUT",
      body: { content, expected_revision: expectedRevision },
    });
  },

  publishProtocol(id: string, message: string): Promise<Version> {
    return request<Version>(`/api/protocols/${id}/versions`, { method: "POST", body: { message } });
  },

  /** References against the design's draft; with a condition set, a script's sources too. */
  checkProtocol(id: string, conditionSetId?: string): Promise<ProtocolCheck> {
    return request<ProtocolCheck>(`/api/protocols/${id}/check`, { query: { condition_set_id: conditionSetId } });
  },

  getDraft(designId: string): Promise<Draft> {
    return request<Draft>(`/api/designs/${designId}/draft`);
  },

  updateDraft(
    designId: string,
    content: Record<string, unknown>,
    expectedRevision: number,
  ): Promise<Draft> {
    return request<Draft>(`/api/designs/${designId}/draft`, {
      method: "PUT",
      body: { content, expected_revision: expectedRevision },
    });
  },

  /** Set the draft back to the latest published version, discarding unpublished changes. */
  revertDraft(designId: string, expectedRevision: number): Promise<Draft> {
    return request<Draft>(`/api/designs/${designId}/draft/revert`, {
      method: "POST",
      body: { expected_revision: expectedRevision },
    });
  },

  publish(designId: string, message: string): Promise<Version> {
    return request<Version>(`/api/designs/${designId}/versions`, {
      method: "POST",
      body: { message },
    });
  },

  listVersions(designId: string): Promise<VersionSummary[]> {
    return request<VersionSummary[]>(`/api/designs/${designId}/versions`);
  },

  getVersion(designId: string, versionId: string): Promise<Version> {
    return request<Version>(`/api/designs/${designId}/versions/${versionId}`);
  },

  diffVersions(designId: string, fromVersionId: string, toVersionId: string): Promise<Diff> {
    return request<Diff>(`/api/designs/${designId}/diff`, {
      query: { from_version_id: fromVersionId, to_version_id: toVersionId },
    });
  },

  /** What is unpublished: the draft against a version. */
  diffDraft(designId: string, versionId: string): Promise<Diff> {
    return request<Diff>(`/api/designs/${designId}/draft/diff`, {
      query: { version_id: versionId },
    });
  },

  /** Rename a design. The name is the container's, so no version changes (invariant 1). */
  renameDesign(designId: string, name: string): Promise<Design> {
    return request<Design>(`/api/designs/${designId}`, { method: "PATCH", body: { name } });
  },

  /** Switch a design between microfluidic device and tubing set (D34). Clears its profile. */
  setKind(designId: string, kind: Design["kind"]): Promise<Design> {
    return request<Design>(`/api/designs/${designId}/kind`, { method: "PUT", body: { kind } });
  },

  /** What the design shows volumes and flows in, over its project's (D49); null inherits everything. */
  setDesignDisplayUnits(designId: string, displayUnits: DisplayUnitsBody): Promise<Design> {
    return request<Design>(`/api/designs/${designId}/display-units`, {
      method: "PUT",
      body: { display_units: displayUnits },
    });
  },
  /** What a project's designs show volumes and flows in, unless they say (D49). */
  setProjectDisplayUnits(projectId: string, displayUnits: DisplayUnitsBody): Promise<Project> {
    return request<Project>(`/api/projects/${projectId}/display-units`, {
      method: "PUT",
      body: { display_units: displayUnits },
    });
  },
  /** Choose the profile the design is checked against; null clears it. */
  setProfile(designId: string, profileVersionId: string | null): Promise<Design> {
    return request<Design>(`/api/designs/${designId}/profile`, {
      method: "PUT",
      body: { profile_version_id: profileVersionId },
    });
  },

  /** Check the saved draft against its profile. Read-only; findings come back with a 200. */
  checkDesign(designId: string): Promise<CheckReport> {
    return request<CheckReport>(`/api/designs/${designId}/check`);
  },

  /** The shared library plus, when named, a project's own entities. */
  listLibrary(kind: LibraryKind, projectId?: string): Promise<LibraryEntity[]> {
    return request<LibraryEntity[]>(`/api/library/${kind}`, {
      query: { project_id: projectId },
    });
  },

  getLibraryVersion(versionId: string): Promise<LibraryVersion> {
    return request<LibraryVersion>(`/api/library/versions/${versionId}`);
  },

  getLibraryEntity(entityId: string): Promise<LibraryEntityDetail> {
    return request<LibraryEntityDetail>(`/api/library/entities/${entityId}`);
  },

  /** Remove a project block no design uses. Owners only; its versions are kept. */
  removeBlock(entityId: string): Promise<void> {
    return request<void>(`/api/library/entities/${entityId}`, { method: "DELETE" });
  },

  listLibraryVersions(entityId: string): Promise<LibraryVersionSummary[]> {
    return request<LibraryVersionSummary[]>(`/api/library/entities/${entityId}/versions`);
  },

  /** A library entity's working copy: how a block definition is edited on its own (D20). */
  getLibraryDraft(entityId: string): Promise<LibraryDraft> {
    return request<LibraryDraft>(`/api/library/entities/${entityId}/draft`);
  },

  /** Saved but unpublished changes to a library entity; null if never published. */
  libraryDraftDiff(entityId: string): Promise<Diff | null> {
    return request<Diff | null>(`/api/library/entities/${entityId}/draft/diff`);
  },

  updateLibraryDraft(
    entityId: string,
    content: Record<string, unknown>,
    expectedRevision: number,
  ): Promise<LibraryDraft> {
    return request<LibraryDraft>(`/api/library/entities/${entityId}/draft`, {
      method: "PUT",
      body: { content, expected_revision: expectedRevision },
    });
  },

  /** Freeze a library draft as a version. For a block, what raises the update prompt. */
  publishLibraryVersion(entityId: string, message: string): Promise<LibraryVersion> {
    return request<LibraryVersion>(`/api/library/entities/${entityId}/versions`, {
      method: "POST",
      body: { message },
    });
  },

  // --- Blocks (D20). Each writes the draft and returns it. ---

  /** Save a selection as a block, scoped to the project or the shared library (8.1, 8.2). */
  saveBlock(
    designId: string,
    body: { selection: string[]; name: string; library_scoped: boolean; summary: string; expected_revision: number },
  ): Promise<SavedBlock> {
    return request<SavedBlock>(`/api/designs/${designId}/blocks`, {
      method: "POST",
      body: { ...body, exposed_ports: null, replace_selection: true },
    });
  },

  placeBlock(
    designId: string,
    definitionVersionId: string,
    expectedRevision: number,
    at: { x: number; y: number } | null,
  ): Promise<PlacedBlock> {
    return request<PlacedBlock>(`/api/designs/${designId}/blocks/place`, {
      method: "POST",
      body: { definition_version_id: definitionVersionId, expected_revision: expectedRevision, at },
    });
  },

  detachBlock(designId: string, instance: string, expectedRevision: number): Promise<DetachedBlock> {
    return request<DetachedBlock>(`/api/designs/${designId}/blocks/${encodeURIComponent(instance)}/detach`, {
      method: "POST",
      body: { expected_revision: expectedRevision },
    });
  },

  /** Update an instance to its definition's latest version: always explicit (8.8). */
  updateBlock(designId: string, instance: string, expectedRevision: number): Promise<UpdatedBlock> {
    return request<UpdatedBlock>(`/api/designs/${designId}/blocks/${encodeURIComponent(instance)}/update`, {
      method: "POST",
      body: { expected_revision: expectedRevision, to_version_id: null },
    });
  },

  /** Instances whose definition has a newer version: the prompt, never applied. */
  blockUpdates(designId: string): Promise<BlockUpdate[]> {
    return request<BlockUpdate[]>(`/api/designs/${designId}/blocks/updates`);
  },

  listActivity(query: { project_id?: string; limit?: number } = {}): Promise<ActivityEntry[]> {
    return request<ActivityEntry[]>("/api/activity", { query });
  },

  enqueueEcho(message: string, steps: number): Promise<Job> {
    return request<Job>("/api/jobs/echo", {
      method: "POST",
      body: { message, steps, project_id: null, idempotency_key: null },
    });
  },

  getJob(jobId: string): Promise<Job> {
    return request<Job>(`/api/jobs/${jobId}`);
  },

  listJobs(): Promise<Job[]> {
    return request<Job[]>("/api/jobs");
  },
};
